import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useAuth0Context } from "@/contexts/Auth0Context";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  FileText,
  Zap,
  Shield,
  Check,
  ArrowRight,
  Play,
  X,
  ExternalLink,
} from "lucide-react";
import { SEO, SEOConfigs } from "@/components/SEO";
import { Footer } from "@/components/layout/Footer";

export const Landing = () => {
  const { isAuthenticated: legacyAuth, logout: legacyLogout } = useAuth();
  const {
    isAuthenticated: auth0Authenticated,
    loginWithRedirect,
    isLoading,
    user: auth0User,
    logout: auth0Logout,
  } = useAuth0Context();

  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  const scrollToSignupSection = () => {
    const el = document.getElementById("signup-options");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.location.href = "#signup-options";
    }
  };

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isVideoModalOpen) {
        setIsVideoModalOpen(false);
      }
    };

    if (isVideoModalOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden"; // Prevent background scrolling
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isVideoModalOpen]);

  const handleSignIn = async () => {
    try {
      await loginWithRedirect();
    } catch (error) {
      console.error("Sign in error:", error);
    }
  };

  const handleSignUp = async () => {
    try {
      // For Auth0, we can pass screen_hint=signup to show signup form
      await loginWithRedirect();
    } catch (error) {
      console.error("Sign up error:", error);
    }
  };

  // Show loading state while Auth0 is initializing
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <SEO {...SEOConfigs.landing} url="/" />
      <div className="flex flex-col min-h-screen text-white landing-dark">
        {/* Navigation */}
        <nav className="fixed top-0 w-full z-50 bg-gray-900/80 backdrop-blur-lg border-b border-gray-700">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center">
                <a href="/" className="text-2xl font-bold gradient-text-purple">
                  SnapLading
                </a>
              </div>
              <div className="flex items-center gap-4">
                {auth0Authenticated || legacyAuth ? (
                  <>
                    <Button
                      size="sm"
                      className="button-purple button-modern"
                      asChild
                    >
                      <a href="/app/upload">Dashboard</a>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-gray-300 hover:text-white"
                      onClick={() => {
                        if (auth0Authenticated) {
                          auth0Logout();
                        } else {
                          legacyLogout();
                        }
                      }}
                    >
                      Sign Out
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-gray-300 hover:text-white"
                      onClick={handleSignIn}
                    >
                      Sign In
                    </Button>
                    <Button
                      size="sm"
                      className="button-purple button-modern"
                      onClick={handleSignUp}
                    >
                      Sign Up
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section with Video Background */}
        <section className="pt-32 pb-20 px-4 lg:px-8 relative min-h-screen flex items-center flex-1">
          {/* Background Video */}
          <video
            src="/videos/fullsection.mp4"
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            style={{ zIndex: -2 }}
          >
            Your browser does not support the video tag.
          </video>

          {/* Video Overlay for better text readability */}
          <div
            className="absolute inset-0"
            style={{
              background: "rgba(0, 0, 0, 0.4)",
              zIndex: -1,
            }}
          ></div>

          <div className="container mx-auto max-w-7xl relative z-10">
            <div className="bg-black/20 backdrop-blur-sm rounded-2xl p-8 md:p-12 border border-white/10 shadow-2xl">
              <div className="text-center space-y-8">
                {/* Main Heading */}
                <h1 className="text-6xl sm:text-7xl lg:text-8xl font-bold tracking-tight hero-text-animate">
                  <span className="text-white">SNAP </span>
                  <span className="gradient-text-purple">LADING</span>
                </h1>

                {/* Subheading */}
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-purple-300 hero-text-animate">
                  AI Agent Bill of Lading Processing
                </h2>

                {/* Subheading */}
                <p className="text-xl sm:text-2xl text-gray-300 max-w-3xl mx-auto hero-text-animate">
                  AI agents perform data entry on bills of lading in seconds
                  directly into your current TMS via EDI X12 211 (BOL), 204
                  (Pickup request). No custom development required!
                </p>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4 hero-text-animate">
                  <Button
                    size="lg"
                    className="min-w-[200px] text-lg h-14 button-purple button-modern"
                    onClick={scrollToSignupSection}
                  >
                    Start Free Trial
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="min-w-[200px] text-lg h-14 button-purple-outline border-purple-600"
                    onClick={() => setIsVideoModalOpen(true)}
                  >
                    <Play className="mr-2 h-5 w-5" />
                    Watch Demo
                  </Button>
                </div>

                {/* Trust Indicators */}
                <div className="flex flex-wrap justify-center gap-8 pt-8 text-sm text-gray-400 hero-text-animate">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-purple-500" />
                    <span>Up to 99.9% Accuracy</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-purple-500" />
                    <span>24/7 Processing</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-purple-500" />
                    <span>SOC 2 Compliant</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Video Demo Section */}
        <section className="py-20 px-4 lg:px-8 bg-gray-900">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center space-y-4 mb-12">
              <h2 className="text-4xl sm:text-5xl font-bold text-white">
                See SnapLading in Action
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Watch how our AI agent processes bills of lading in seconds,
                transforming your logistics workflow with cutting-edge
                technology.
              </p>
            </div>

            <div className="relative max-w-4xl mx-auto">
              <div className="relative overflow-hidden rounded-2xl shadow-2xl bg-gray-800 border border-gray-700">
                <video
                  controls
                  className="w-full h-auto"
                  poster="/images/video-poster.jpg"
                  preload="metadata"
                  onLoadStart={(e) => {
                    // Ensure video starts unmuted
                    const video = e.target as HTMLVideoElement;
                    video.muted = false;
                    video.volume = 1.0;
                  }}
                >
                  <source
                    src="/videos/snapladingdemovideo.mp4"
                    type="video/mp4"
                  />
                  Your browser does not support the video tag.
                </video>
              </div>

              {/* Play Button Overlay */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <Button
                  size="lg"
                  className="pointer-events-auto bg-purple-600/90 hover:bg-purple-600 text-white rounded-full w-20 h-20 p-0 shadow-lg hover:scale-105 transition-all duration-300"
                  onClick={() => setIsVideoModalOpen(true)}
                >
                  <Play className="w-8 h-8 ml-1" />
                </Button>
              </div>
            </div>

            <div className="text-center mt-8">
              <Button
                size="lg"
                className="button-purple button-modern"
                onClick={() => setIsVideoModalOpen(true)}
              >
                <Play className="mr-2 h-5 w-5" />
                Watch Full Demo
              </Button>
            </div>
          </div>
        </section>

        {/* Platform Explanation Section */}
        <section
          id="features"
          className="py-20 px-4 lg:px-8 gradient-bg-purple-grey"
        >
          <div className="container mx-auto max-w-7xl">
            <div className="text-center space-y-4 mb-16">
              <h2 className="text-4xl sm:text-5xl font-bold text-white">
                Integrate AI into your legacy TMS/ERP
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                SnapLading seamlessly feeds into your existing systems, bringing
                modern AI capabilities to your current workflows via EDI without
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <Card className="relative overflow-hidden group hover:shadow-lg transition-all duration-300 card-lift bg-gray-900 border-gray-800 card-glow-purple">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-600/10 to-purple-600/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <CardHeader>
                  <div className="w-12 h-12 bg-purple-600/20 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <FileText className="w-6 h-6 text-purple-400" />
                  </div>
                  <CardTitle className="text-2xl text-white">
                    Smart Data Entry AI Agent
                  </CardTitle>
                  <CardDescription className="text-base text-gray-400">
                    Our AI agent extracts printed text, handwritten text,
                    barcodes, and QR codes from bills of lading with 99.9%
                    accuracy from any bill of lading.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-500">
                    Advanced computer vision technology reads handwritten and
                    printed text from any bill of lading format. Handles poor
                    quality scans, faded documents, and complex layouts with
                    industry-leading precision.
                  </p>
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden group hover:shadow-lg transition-all duration-300 card-lift bg-gray-900 border-gray-800 card-glow-purple">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-600/10 to-purple-600/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <CardHeader>
                  <div className="w-12 h-12 bg-purple-600/20 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Zap className="w-6 h-6 text-purple-400" />
                  </div>
                  <CardTitle className="text-2xl text-white">
                    Instant EDI Generation
                  </CardTitle>
                  <CardDescription className="text-base text-gray-400">
                    Convert extracted data to EDI X12 211 (Bill of lading) and
                    204 (Pickup request) instantly, with support for multiple
                    EDI versions.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-500">
                    Seamlessly transforms extracted data into standardized EDI
                    formats including X12 204 pickup requests and 211 bills of
                    lading. Compatible with all major EDI versions and trading
                    partner requirements.
                  </p>
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden group hover:shadow-lg transition-all duration-300 card-lift bg-gray-900 border-gray-800 card-glow-purple">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-600/10 to-purple-600/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <CardHeader>
                  <div className="w-12 h-12 bg-purple-600/20 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Shield className="w-6 h-6 text-purple-400" />
                  </div>
                  <CardTitle className="text-2xl text-white">
                    Human in the loop or direct API
                  </CardTitle>
                  <CardDescription className="text-base text-gray-400">
                    Up to 10x the productivity of your human data entry team by
                    augmenting them with simple to use drag and drop billing, or
                    feed bill images directly into SnapLading's API for 100%
                    automated processing.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-500">
                    Eliminate manual typing of data into your TMS. Minimize data
                    entry errors. Choose between manual review for maximum
                    accuracy or scenarios or fully automated API processing for
                    instant results. Flexible integration options including
                    SFTP, REST APIs, and webhook notifications to fit your
                    workflow. Bank-level encryption, SOC 2 compliance, and
                    complete data isolation ensure your sensitive logistics data
                    stays secure.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section
          id="pricing"
          className="py-20 px-4 lg:px-8 gradient-bg-purple-grey"
        >
          <div className="container mx-auto max-w-5xl">
            <div className="text-center space-y-4 mb-16">
              <h2 className="text-4xl sm:text-5xl font-bold text-white">
                Simple, usage-based pricing
              </h2>
              <p className="text-xl text-gray-300">
                Pay only for what you process. No setup fees, no hidden costs.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <Card className="relative overflow-hidden hover:shadow-xl transition-all duration-300 card-lift bg-gray-900 border-gray-800 card-glow-purple">
                <div className="absolute top-0 right-0 w-32 h-32 bg-purple-600/10 rounded-full -mr-16 -mt-16" />
                <CardHeader className="pb-8">
                  <CardTitle className="text-3xl text-white">
                    Good as a Human
                  </CardTitle>
                  <div className="mt-4">
                    <span className="text-5xl font-bold text-white">15¢</span>
                    <span className="text-xl text-gray-400 ml-2">
                      USD per bill
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-gray-400">
                    Perfect for small to medium-sized businesses with occasional
                    bill of lading processing needs. User interface for manually
                    uploading and processing bills.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300 relative">
                        Up to 99% accuracy rate
                        <span className="absolute -top-1 -right-4 text-xs bg-purple-600 text-white rounded-full w-4 h-4 flex items-center justify-center font-bold">
                          1
                        </span>
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300">
                        Process unlimited bills/day
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300">
                        100% scalable. Process 1000s of bills in parallel in
                        under 2 minutes
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300">
                        Generate EDI X12 204 pickup requests
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300">
                        Generate EDI X12 211 bills of lading
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300">SFTP downloads</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300">API downloads</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300">Report missing data</span>
                    </li>
                  </ul>
                  <p className="text-sm text-purple-300 text-center font-medium">
                    Test your first 50 bills for free!
                  </p>
                  <Button
                    className="w-full button-purple button-modern"
                    size="lg"
                    onClick={scrollToSignupSection}
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden border-purple-600/50 hover:shadow-[0_0_80px_rgba(255,255,255,0.15)] transition-all duration-300 card-lift bg-gray-900 card-glow-purple shadow-[0_0_50px_rgba(255,255,255,0.1)]">
                <div className="absolute top-0 right-0 w-32 h-32 bg-purple-600/20 rounded-full -mr-16 -mt-16" />
                <div className="absolute inset-0 bg-white/5 rounded-lg pointer-events-none" />
                <Badge
                  className="absolute top-4 right-4 badge-glow-purple bg-purple-600 text-white"
                  variant="default"
                >
                  RECOMMENDED
                </Badge>
                <CardHeader className="pb-8">
                  <CardTitle className="text-3xl text-white">
                    Better than a Human
                  </CardTitle>
                  <div className="mt-4">
                    <span className="text-5xl font-bold text-white">20¢</span>
                    <span className="text-xl text-gray-400 ml-2">
                      USD per bill
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-gray-400">
                    Perfect for high-volume logistics operations and enterprise
                    customers. Includes advanced AI features, custom
                    integrations, and premium support for mission-critical
                    processing needs.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300 relative">
                        Up to 99.9% accuracy rate
                        <span className="absolute -top-1 -right-4 text-xs bg-purple-600 text-white rounded-full w-4 h-4 flex items-center justify-center font-bold">
                          1
                        </span>
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300">
                        Process unlimited bills/day
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300">
                        100% scalable. Process 1000s of bills in parallel in
                        under 2 minutes
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300">
                        Generate EDI X12 204 pickup requests
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300">
                        Generate EDI X12 211 bills of lading
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300">
                        SFTP downloads with download logs
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300">API downloads</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300">Report missing data</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300">
                        Custom ISA sender IDs
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300">
                        Custom extraction for shipment PRO numbers
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300">
                        Advanced AI features
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300">
                        Extract data from barcodes and QR codes
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300">
                        Optional Microsoft Azure billing
                      </span>
                    </li>
                  </ul>
                  <p className="text-sm text-purple-300 text-center font-medium">
                    Test your first 50 bills for free!
                  </p>
                  <Button
                    className="w-full button-purple button-modern"
                    size="lg"
                    onClick={scrollToSignupSection}
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Accuracy Footnote */}
            <div className="text-center mt-12 pt-8 border-t border-gray-700">
              <p className="text-sm text-white">
                <span className="inline-flex items-center justify-center w-4 h-4 text-xs bg-purple-600 text-white rounded-full mr-1 font-bold">
                  1
                </span>
                Accuracy is dependent on the quality of the document. Reads
                English, Spanish, and French. Typed and handwritten.
              </p>
            </div>
          </div>
        </section>

        {/* Signup Options Section */}
        <section
          id="signup-options"
          className="py-20 px-4 lg:px-8 gradient-bg-purple-grey"
        >
          <div className="container mx-auto max-w-6xl">
            <div className="text-center space-y-4 mb-12">
              <h2 className="text-4xl sm:text-5xl font-bold text-white">
                Choose How You’d Like to Sign Up
              </h2>
              <p className="text-xl text-gray-300">
                Use Stripe for instant signup and a-la-carte credits, or
                subscribe via Microsoft Azure Marketplace.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Stripe Signup */}
              <Card className="relative overflow-hidden hover:shadow-xl transition-all duration-300 card-lift bg-gray-900 border-gray-800 card-glow-purple">
                <div className="absolute top-0 right-0 w-32 h-32 bg-purple-600/10 rounded-full -mr-16 -mt-16" />
                <CardHeader className="pb-4">
                  <CardTitle className="text-2xl text-white">
                    Sign up with Stripe
                  </CardTitle>
                  <CardDescription className="text-base text-gray-400">
                    Create your account with email. A $10 welcome credit will be
                    applied. Purchase additional credits a-la-carte with Stripe
                    anytime.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-2 text-gray-300 text-sm">
                    <li>• Quick email signup</li>
                    <li>• $10 welcome credit applied after signup</li>
                    <li>• Buy more credits as you need them</li>
                  </ul>
                  <Button
                    className="button-purple button-modern"
                    size="lg"
                    onClick={handleSignUp}
                  >
                    Sign Up with Email
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>

              {/* Azure Marketplace Signup */}
              <Card className="relative overflow-hidden hover:shadow-xl transition-all duration-300 card-lift bg-gray-900 border-gray-800 card-glow-purple">
                <div className="absolute top-0 right-0 w-32 h-32 bg-purple-600/10 rounded-full -mr-16 -mt-16" />
                <CardHeader className="pb-4">
                  <CardTitle className="text-2xl text-white">
                    Sign up with Microsoft Azure
                  </CardTitle>
                  <CardDescription className="text-base text-gray-400">
                    Purchase a bucket of credits through Azure Marketplace and
                    have usage billed to your Azure subscription.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-2 text-gray-300 text-sm">
                    <li>• Use Azure billing and governance</li>
                    <li>• Credits are added to your SnapLading account</li>
                    <li>• Monitor spend in Azure</li>
                  </ul>
                  <Button
                    className="button-purple button-modern"
                    size="lg"
                    asChild
                  >
                    <a
                      href="https://marketplace.microsoft.com/en-us/product/saas/openverify.snaplading_production-preview?tab=Overview&flightCodes=a63322be-b6f1-46fc-83b4-ba17b021a1fe"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open Azure Marketplace
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Footer */}
        <Footer />

        {/* Video Modal */}
        {isVideoModalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setIsVideoModalOpen(false);
              }
            }}
          >
            <div className="relative w-full max-w-6xl mx-4 bg-gray-900 rounded-2xl overflow-hidden shadow-2xl">
              {/* Close Button */}
              <Button
                variant="ghost"
                size="sm"
                className="absolute top-4 right-4 z-10 text-white hover:text-gray-300 bg-black/50 hover:bg-black/70 rounded-full w-10 h-10 p-0"
                onClick={() => setIsVideoModalOpen(false)}
              >
                <X className="w-5 h-5" />
              </Button>

              {/* Video */}
              <video
                controls
                autoPlay
                className="w-full h-auto"
                preload="metadata"
                onLoadStart={(e) => {
                  // Ensure video starts unmuted
                  const video = e.target as HTMLVideoElement;
                  video.muted = false;
                  video.volume = 1.0;
                }}
              >
                <source
                  src="/videos/snapladingdemovideo.mp4"
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
